import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ShieldCheck, CheckCircle, ArrowRight } from "lucide-react";
import { useConsultationEnabled } from "@/hooks/useConsultationEnabled";

const ResilienceService = () => {
  const consultationEnabled = useConsultationEnabled();
  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-24 pb-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <ShieldCheck className="w-12 h-12 text-accent" />
              <h1 className="text-4xl md:text-5xl font-bold">Resilience Products</h1>
            </div>
            
            <p className="text-xl text-muted-foreground mb-8">
              Implement tools and solutions that strengthen your security posture and resilience.
            </p>

            <div className="prose prose-lg max-w-none mb-12">
              <h2 className="text-2xl font-bold mb-4">Strengthening Your Defences</h2>
              <p className="mb-6">
                Our resilience products and solutions help organisations build robust security infrastructures that can withstand and recover from cyber incidents. We provide technology selection, implementation, and optimisation services for a wide range of security tools, ensuring you get maximum value from your security investments.
              </p>

              <h2 className="text-2xl font-bold mb-4">Technology Selection Process</h2>
              <div className="bg-secondary/20 rounded-lg p-6 mb-6">
                <p className="mb-3"><strong>Our vendor-neutral methodology:</strong></p>
                <ul className="space-y-2">
                  <li><strong>Requirements analysis:</strong> Understanding your specific needs, constraints, and objectives</li>
                  <li><strong>Market evaluation:</strong> Assessing 3-5 solutions against your criteria</li>
                  <li><strong>Proof of concept testing:</strong> Hands-on evaluation in your environment</li>
                  <li><strong>Cost-benefit analysis:</strong> TCO calculation including licensing, implementation, and operations</li>
                  <li><strong>Implementation roadmap:</strong> Phased deployment plan with clear milestones</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold mb-4">Solutions We Implement</h2>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Backup and disaster recovery:</strong> Automated backup solutions with tested recovery procedures (RPO/RTO optimised)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>SIEM (Security Information and Event Management):</strong> Centralised logging, correlation, and threat detection</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>EDR (Endpoint Detection and Response):</strong> Advanced endpoint protection with automated response capabilities</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Multi-factor authentication:</strong> Modern MFA solutions including passwordless options</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Email security and anti-phishing:</strong> Advanced threat protection with user training integration</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-1 flex-shrink-0" />
                  <span><strong>Network security monitoring:</strong> IDS/IPS, network traffic analysis, and anomaly detection</span>
                </li>
              </ul>

              <h2 className="text-2xl font-bold mb-4">Implementation Timelines</h2>
              <div className="space-y-2 mb-6">
                <p>• <strong>SIEM deployment:</strong> 4-6 weeks for 100-500 user organisations</p>
                <p>• <strong>EDR rollout:</strong> 2-4 weeks for 100-500 endpoints</p>
                <p>• <strong>Email security:</strong> 1-2 weeks including user communication</p>
                <p>• <strong>MFA implementation:</strong> 2-3 weeks with phased user onboarding</p>
                <p>• <strong>Backup solution:</strong> 3-4 weeks including testing and documentation</p>
              </div>

              <h2 className="text-2xl font-bold mb-4">Optimisation Services</h2>
              <div className="bg-accent/10 rounded-lg p-6 mb-6">
                <p className="mb-3">We don't just deploy tools—we optimise them for your environment:</p>
                <ul className="space-y-2">
                  <li>• <strong>Post-deployment tuning:</strong> Reduce alert fatigue by 80% through intelligent filtering</li>
                  <li>• <strong>Automation playbooks:</strong> Achieve 95% automation for common security responses</li>
                  <li>• <strong>Integration optimisation:</strong> Connect security tools for unified visibility</li>
                  <li>• <strong>Performance monitoring:</strong> Ongoing optimisation to maintain efficiency</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold mb-4">Return on Investment</h2>
              <div className="space-y-3">
                <div className="border-l-4 border-accent pl-4">
                  <p className="font-semibold">60% reduction in security operations time</p>
                  <p className="text-sm text-muted-foreground">Through automation and tool consolidation</p>
                </div>
                <div className="border-l-4 border-accent pl-4">
                  <p className="font-semibold">90% faster threat detection</p>
                  <p className="text-sm text-muted-foreground">With properly tuned SIEM and EDR solutions</p>
                </div>
                <div className="border-l-4 border-accent pl-4">
                  <p className="font-semibold">80% reduction in successful phishing attempts</p>
                  <p className="text-sm text-muted-foreground">Using advanced email security with user training</p>
                </div>
              </div>
            </div>

            <div className="bg-secondary/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold mb-4">Ready to enhance your security infrastructure?</h3>
              <p className="text-muted-foreground mb-6">
                Let's discuss which resilience products and solutions are right for your organisation.
              </p>
              {consultationEnabled && (
              <a 
                href="https://outlook.office365.com/owa/calendar/HazelMcPherson4FOXSecurity@4fox.uk/bookings/" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button size="lg" className="bg-accent hover:bg-accent/90">
                  Book a Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ResilienceService;
